import { useState, Fragment } from 'react';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';

import {
  Box,
  Button,
  IconButton,
  Table,
  TablePagination,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Paper,
  Menu,
  MenuItem,
  Link,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';

// Icons
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import EditIcon from '@mui/icons-material/Edit';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Custom Components
import { ClientPropertiesDrawer } from '../..';

// Utils
import messages from '../../../static/messages';
import { nullClear, nullClearObject } from '../../../utils/cleaners';

// Services
import clientsServices from '../../../services/clientsServices';

// Hooks
import usePagination from '../../../hooks/usePagination';
import useDrawer from '../../../hooks/useDrawer';

// Styles
import { actions_column, table_container_base } from '../../../static/styles';
import { button } from '../../../static/theme-styles';

const menuPaperProps = {
  elevation: 0,
  sx: {
    overflow: 'visible',
    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
    mt: 1.5,
    '& .MuiSvgIcon-root': { width: 25, height: 25, mr: 1 },
  },
};

const base_property = {
  property: { typeName: '', address: '' },
  relation: '',
};

// Mobile Property Card
const MobilePropertyCard = ({ property, onEdit, onDelete }) => (
  <Card variant="outlined" sx={{ mb: 2, borderColor: '#4caf50' }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Link href={`/properties/${property.property.id}`} underline="none" sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <HomeIcon sx={{ color: '#4caf50', fontSize: 20 }} />
            <Chip label={property.property.typeName || 'Property'} size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }} />
            {property.relation && (
              <Chip label={property.relation} size="small" variant="outlined" sx={{ color: '#666' }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOnIcon sx={{ color: '#757575', fontSize: 18 }} />
            <Typography variant="body2" sx={{ color: '#333' }}>
              {property.property.address}
            </Typography>
            <ChevronRightIcon sx={{ color: '#999', fontSize: 18 }} />
          </Box>
        </Link>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" onClick={() => onEdit(property.property.id)} sx={{ bgcolor: '#e3f2fd' }}>
            <EditIcon sx={{ fontSize: 18, color: '#1976d2' }} />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(property.property.id)} sx={{ bgcolor: '#ffebee' }}>
            <DeleteForeverIcon sx={{ fontSize: 18, color: '#f44336' }} />
          </IconButton>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const ClientPropertiesTable = ({ properties, id, refresh }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [drawerMode, setDrawerMode] = useState('find');
  const [currentProperty, setCurrentProperty] = useState();
  const [anchorEl, setAnchorEl] = useState(null);
  const openMore = Boolean(anchorEl);

  const confirm = useConfirm();
  const { drawer, openDrawer, foolCheck } = useDrawer(currentProperty);
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination();

  const onCloseDrawer = async (resetErrors) => {
    const confirmed = await foolCheck();
    if (confirmed === true) {
      setDrawerMode('find');
      setCurrentProperty(null);
      resetErrors && resetErrors();
    }
  };

  const onAddProperty = () => { setCurrentProperty(base_property); setDrawerMode('new'); openDrawer(); };
  const onFindProperty = () => { setCurrentProperty(base_property); openDrawer(); };

  const onEditProperty = (propId) => {
    const property = properties.filter((el) => el.property.id === propId)[0];
    delete property.property.createdAt;
    delete property.property.deletedAt;
    delete property.property.updatedAt;
    setCurrentProperty(property);
    setDrawerMode('edit');
    openDrawer();
  };

  const handleClick = (event) => setAnchorEl(event.currentTarget);

  const handleDelete = (propId) => {
    confirm({
      title: messages.confirm_message.remove,
      description: messages.confirm_message.remove_description('Property'),
    })
      .then(() => removeProperty(propId))
      .catch(() => toast.info(messages.cancel_message));
  };

  const addNewProperty = async () => {
    delete currentProperty.isEdited;
    const filterForNullValue = nullClear(properties);
    const updatedProperties = { properties: [...filterForNullValue, currentProperty] };
    try {
      const response = await clientsServices.update(id, updatedProperties);
      if (response.status === 200) { onCloseDrawer(); toast.success(messages.client.update); refresh(); }
    } catch (error) { toast.error(messages.errors.error_try_again); console.log(error); }
  };

  const removeProperty = async (propertyId) => {
    const updatedList = { properties: [...nullClear(properties.filter((el) => el.property.id !== propertyId))] };
    try {
      const response = await clientsServices.update(id, updatedList);
      if (response.status === 200) { toast.success(messages.client.update); refresh(); }
    } catch (error) { toast.error(messages.errors.error_try_again); console.log(error); }
  };

  const updateProperty = async () => {
    delete currentProperty.isEdited;
    const filterForNullValue = nullClear(properties);
    const updatedProperties = {
      properties: [...filterForNullValue.filter((el) => el.id !== currentProperty.id), nullClearObject(currentProperty)],
    };
    try {
      const response = await clientsServices.update(id, updatedProperties);
      if (response.status === 200) { onCloseDrawer(); toast.success(messages.client.update); refresh(); }
    } catch (error) { toast.error(messages.errors.error_try_again); console.log(error); }
  };

  return (
    <Fragment>
      <Button variant="contained" onClick={handleClick} endIcon={<AddIcon />} sx={{ ...button('secondary'), mb: 2, width: isMobile ? '100%' : 'auto' }}>
        Add Property
      </Button>
      <Menu anchorEl={anchorEl} open={openMore} onClose={() => setAnchorEl(null)} onClick={() => setAnchorEl(null)} PaperProps={menuPaperProps}>
        <MenuItem onClick={onFindProperty}><SearchIcon /> Existing Property</MenuItem>
        <MenuItem onClick={onAddProperty}><HomeIcon /> New Property</MenuItem>
      </Menu>

      {/* Desktop Table */}
      {!isMobile && (
        <TableContainer component={Paper} sx={table_container_base}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="20%">Type</TableCell>
                <TableCell width="55%">Address</TableCell>
                <TableCell width="20%">Relation</TableCell>
                <TableCell width="5%">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {properties.length ? (
                properties.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((property) => (
                  <TableRow key={property.id}>
                    <TableCell><Link href={`/properties/${property.property.id}`} underline="none" color="rgba(0,0,0,0.87)" className="full-col-flex-link">{property.property.typeName}</Link></TableCell>
                    <TableCell><Link href={`/properties/${property.property.id}`} underline="none" color="rgba(0,0,0,0.87)" className="full-col-flex-link">{property.property.address}</Link></TableCell>
                    <TableCell><Link href={`/properties/${property.property.id}`} underline="none" color="rgba(0,0,0,0.87)" className="full-col-flex-link">{property.relation}</Link></TableCell>
                    <TableCell sx={actions_column}>
                      <Tooltip title="Edit"><IconButton onClick={() => onEditProperty(property.property.id)}><EditIcon /></IconButton></Tooltip>
                      <Tooltip title="Remove"><IconButton onClick={() => handleDelete(property.property.id)}><DeleteForeverIcon /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center' }}><strong>No Data</strong></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Mobile Cards */}
      {isMobile && (
        <Box>
          {properties.length ? (
            properties.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((property) => (
              <MobilePropertyCard key={property.id} property={property} onEdit={onEditProperty} onDelete={handleDelete} />
            ))
          ) : (
            <Typography variant="body2" sx={{ textAlign: 'center', py: 3 }}>No properties</Typography>
          )}
        </Box>
      )}

      {!isMobile && (
        <TablePagination rowsPerPageOptions={[10, 25, 50]} component="div" count={properties.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
      )}

      {isMobile && properties.length > rowsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
          <Button size="small" variant="outlined" onClick={() => handleChangePage(null, page - 1)} disabled={page === 0}>Prev</Button>
          <Typography variant="body2" sx={{ alignSelf: 'center' }}>{page + 1}/{Math.ceil(properties.length / rowsPerPage)}</Typography>
          <Button size="small" variant="outlined" onClick={() => handleChangePage(null, page + 1)} disabled={(page + 1) * rowsPerPage >= properties.length}>Next</Button>
        </Box>
      )}

      <ClientPropertiesDrawer drawer={drawer} onCloseDrawer={onCloseDrawer} drawerMode={drawerMode} currentProperty={currentProperty} setCurrentProperty={setCurrentProperty} addNewProperty={addNewProperty} updateProperty={updateProperty} />
    </Fragment>
  );
};

export default ClientPropertiesTable;
